import { toDecimal } from '@/lib/site-viewer/utils'
import { fovToSocketUnits } from '@/lib/site-viewer/fov-conversion'
import type { IncludeSiteCamera, Slide } from '@/entities/deck'
import type { DeepReadonly } from 'vue'

export interface UseSiteSlide {
  updateSlide(slide: Slide | DeepReadonly<Slide>, update: Partial<Slide & IncludeSiteCamera>): Promise<Partial<Slide>>
  insertSlides(slides: Array<Partial<Slide & IncludeSiteCamera>>): Promise<Partial<Slide>[]>
}

export function useSiteSlide(): UseSiteSlide {
  async function insertSlides(slides: Array<Partial<Slide & IncludeSiteCamera>>): Promise<Partial<Slide>[]> {
    return Promise.all(
      slides.map(slide => {
        if (slide.type !== 'S' || !slide.site_id || !slide.site_camera) return slide

        delete slide.camera_id // always create a new camera, don't PATCH

        return _updateSiteCamera(slide as Slide, slide.site_camera).then(response => {
          slide.camera_id = response.id
          delete slide.site_camera
          return slide
        })
      })
    )
  }

  async function updateSlide(slide: Slide, update: Partial<Slide & IncludeSiteCamera>): Promise<Partial<Slide>> {
    if (update.site_camera) {
      const { id: camera_id } = await _updateSiteCamera(slide, update.site_camera)

      if (camera_id !== slide.camera_id) {
        update.camera_id = camera_id
      }

      delete update.site_camera
    }
    return update
  }

  const _updateSiteCamera = (slide: Slide, camera: IncludeSiteCamera['site_camera']) => {
    camera.zoom ||= 90 // per VRFY-11265 - default of 90 for zoom when creating a new camera

    const data = {
      hidden_hotspot_ids: camera.hidden_hotspot_ids,
      pan_x: toDecimal(camera.pan_x, 14),
      pan_y: toDecimal(camera.pan_y, 14),
      title: slide.title,
      zoom: fovToSocketUnits(camera.zoom),
    }

    if (slide.camera_id) {
      return $fetch<{ id: number }>(`v2/sites/${slide.site_id}/cameras/${slide.camera_id}`, {
        method: 'PATCH',
        body: data,
        ...useFetchSharedOptions(),
      })
    } else {
      return $fetch<{ id: number }>(`v2/sites/${slide.site_id}/cameras`, {
        method: 'POST',
        body: { ...data, site_id: slide.site_id },
        ...useFetchSharedOptions(),
      })
    }
  }

  return {
    updateSlide,
    insertSlides,
  }
}
